// Lobby, skin shop, game modes + invite links, paywall and the death screen.
// Everything shown here goes through textContent: names and server data never touch innerHTML.
// Points, owned skins and the unlock are server truth; this only displays them and asks the API.
import { NAME_MAX, ROOM_CODE_RE, SKINS, SKIN_BY_ID, isSkinId, type DifficultyId, type SkinId } from '../shared/config';
import { api, ApiError, toProfile, type Profile } from './api';
import { STRINGS, fill, isLang, type Lang, type Strings } from './i18n';

export type PlayMode = 'solo' | 'online' | 'friends';
export interface PlayOptions {
  difficulty: DifficultyId; // solo only
  room?: string; // friends: the private room code
}
export interface SoundLike { muted: boolean; setMuted(muted: boolean): void; click(): void }
export interface UIHandlers {
  onPlay(mode: PlayMode, opts: PlayOptions): void;
  onAgain?(): void; // death screen "Play again" (default: onPlay again with the same mode)
  onLobby?(): void; // death screen "Back to lobby" (default: showLobby)
  onCancel?(): void; // connecting overlay "Cancel" (default: showLobby)
  onProfile?(profile: Profile): void; // every profile update (points feed excite(), etc.)
  onUnlock?(): void; // paid or redeemed: confetti + purchase sound
  onPurchase?(skin: SkinId): void; // a skin was bought
  onView?(view: ViewId): void; // the lobby's 2D / 3D choice (the stored one is read from ui.view at boot)
}
export type ViewId = '3d' | '2d';
export interface DeathInfo {
  title: string;
  stats: string[]; // "Label: value" lines; the value is shown bold
  points?: number; // points earned this life
  delayMs?: number; // default 600: let the death animation play first
  score?: number; // the life's top mass, for "Beat my score" (no button without it)
}
// ?ref=NAME&score=N: a friend's "Beat my score" link
export interface Challenge { by: string; score: number }

const SAVE_DEBOUNCE_MS = 700;
const DEATH_DELAY_MS = 600;
const POINTS_REFRESH_MS = 1200; // after a death, re-read /api/me once the server has awarded the life
const COPIED_MS = 1600;
const VERIFY_TRIES = 4, VERIFY_RETRY_MS = 2000;
const MODES: [string, PlayMode][] = [['modeSolo', 'solo'], ['modeOnline', 'online'], ['modeFriends', 'friends']];
const DIFFS: [string, DifficultyId][] = [['diffEasy', 'easy'], ['diffNormal', 'normal'], ['diffHard', 'hard']];
const PLAY_KEY: Record<PlayMode, 'play' | 'playOnline' | 'playFriends'> = { solo: 'play', online: 'playOnline', friends: 'playFriends' };
const MAX_CHALLENGE_SCORE = 9_999_999;
// every element ui.ts needs from index.html (the HTML test checks they exist)
export const UI_IDS = [
  'lobby', 'name', 'points', 'skins', 'modeSolo', 'modeOnline', 'modeFriends', 'diffRow', 'diffEasy', 'diffNormal', 'diffHard', 'view3d', 'view2d',
  'invite', 'inviteLink', 'copyInvite', 'play', 'paywall', 'buy', 'code', 'redeem', 'payMsg', 'lobbyMsg', 'sound',
  'death', 'deathTitle', 'stats', 'deathPoints', 'again', 'toLobby', 'connecting', 'connectingText', 'cancelJoin',
  'payModal', 'payMount', 'payClose', 'codeBox', 'myCode', 'copyCode', 'codeHint',
  'challenge', 'beatMe', 'copyRoom', 'shareMsg',
] as const;

// the /install card is on screen (shortcut auto-start waits for it)
const installCardOpen = () => !document.getElementById('installCard')?.classList.contains('hidden');

// Stripe.js, loaded only when someone opens the payment window (never on page load)
const STRIPE_JS = 'https://js.stripe.com/v3/';
interface EmbeddedCheckout { mount(el: HTMLElement): void; destroy(): void }
type EmbeddedOpts = { fetchClientSecret: () => Promise<string>; onComplete?: () => void };
// createEmbeddedCheckoutPage is the current name (Stripe 2026-03-25); initEmbeddedCheckout the older one
interface StripeLike { createEmbeddedCheckoutPage?(o: EmbeddedOpts): Promise<EmbeddedCheckout>; initEmbeddedCheckout?(o: EmbeddedOpts): Promise<EmbeddedCheckout> }
type StripeFactory = (key: string) => StripeLike;
let stripeJs: Promise<StripeFactory> | null = null;
function loadStripe(): Promise<StripeFactory> {
  const w = window as unknown as { Stripe?: StripeFactory };
  if (w.Stripe) return Promise.resolve(w.Stripe);
  stripeJs ??= new Promise<StripeFactory>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = STRIPE_JS; s.async = true;
    s.onload = () => (w.Stripe ? resolve(w.Stripe) : reject(new Error('Stripe.js missing')));
    s.onerror = () => reject(new Error('Stripe.js blocked'));
    document.head.appendChild(s);
  }).catch(err => { stripeJs = null; throw err; }); // a later click retries
  return stripeJs;
}

export const DEATH_LINES = [
  '{k} says thanks for the snack.', 'You have been absorbed. Resistance was futile.',
  'Burp. Sincerely, {k}', 'You were a delicious appetizer.', '{k} had you for lunch.',
  'Cell-f destruct complete.', 'Mitosis? More like mito-SIS-n\'t.', 'You got gobbled.',
  'Natural selection has spoken.', 'Somewhere, {k} is licking its membrane.',
  'Rest in pieces. Very small pieces.', 'You were 100% organic, free-range food.',
  'That was not a hug.', '{k} would like to rate you 5 stars.', 'Return to the petri dish.',
  'Plot twist: you were the food.', 'Blob down! Blob down!', 'You\'re in {k}\'s tummy now.',
];

// ========== helpers ==========
export function deathLine(killer: string, rnd: () => number = Math.random): string {
  const line = DEATH_LINES[Math.floor(rnd() * DEATH_LINES.length) % DEATH_LINES.length];
  return line.replaceAll('{k}', killer || 'someone');
}
export function deathStats(d: { peakMass: number; kills: number; aliveSec: number; bestRank: number }): string[] {
  const secs = Math.max(0, Math.floor(d.aliveSec));
  return [
    `Top mass: ${Math.floor(d.peakMass)}`,
    `Players eaten: ${d.kills}`,
    `Survived: ${Math.floor(secs / 60)}m ${secs % 60}s`,
    `Best rank: ${d.bestRank >= 0 && d.bestRank < 99 ? '#' + (d.bestRank + 1) : '-'}`,
  ];
}
// same rule as the Worker: letters, digits, space and _-.! only, 1-15 characters
export function sanitizeName(s: string): string {
  const clean = s.replace(/[^\p{L}\p{N} _\-.!]/gu, '').replace(/\s+/g, ' ').trim();
  return Array.from(clean).slice(0, NAME_MAX).join('').trim();
}
// ?room=CODE (invite link) and ?checkout=cs_... (Stripe success redirect), validated
export function readLaunchParams(search: string): { room: string | null; checkout: string | null; play: 'solo' | 'online' | null } {
  const q = new URLSearchParams(search);
  const room = (q.get('room') || '').trim().toLowerCase(), cs = q.get('checkout') || '', play = q.get('play');
  return { room: ROOM_CODE_RE.test(room) ? room : null, checkout: /^cs_[A-Za-z0-9_]{8,250}$/.test(cs) ? cs : null,
    play: play === 'solo' || play === 'online' ? play : null };
}
export const inviteLink = (room: string, origin: string) => `${origin}/?room=${encodeURIComponent(room)}`;
// ?score= (whole mass, 1..9,999,999) with ?ref= (the challenger's name, cleaned like any name); null without a score
export function readChallenge(search: string): Challenge | null {
  const q = new URLSearchParams(search), s = (q.get('score') || '').trim();
  if (!/^\d{1,7}$/.test(s) || Number(s) < 1) return null;
  return { by: sanitizeName(q.get('ref') || ''), score: Number(s) };
}
// "Beat my score": the home page (or the friends' room) with the challenger's name and top mass
export function challengeLink(origin: string, c: { by: string; score: number; room?: string | null }): string {
  const q = new URLSearchParams();
  if (c.room) q.set('room', c.room);
  q.set('ref', sanitizeName(c.by) || 'friend');
  q.set('score', String(Math.max(1, Math.min(MAX_CHALLENGE_SCORE, Math.floor(c.score)))));
  return `${origin}/?${q.toString()}`;
}
// Macs, iPhones and iPads copy with ⌘C (iPadOS reports itself as a Mac)
interface NavLike { platform?: string; userAgent?: string; userAgentData?: { platform?: string } }
export function isApple(nav: NavLike | undefined): boolean {
  const p = nav?.userAgentData?.platform || nav?.platform || nav?.userAgent || '';
  return /mac|iphone|ipad|ipod|\bios\b/i.test(p);
}
export const copyKeyHint = (nav: NavLike | undefined) => (isApple(nav) ? 'Press ⌘C' : 'Press Ctrl+C');
export function isStripeCheckoutUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && (u.hostname === 'checkout.stripe.com' || u.hostname.endsWith('.stripe.com'));
  } catch { return false; }
}
function darken(hex: string, f = 0.8): string {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) * f, g = ((n >> 8) & 255) * f, b = (n & 255) * f;
  return `rgb(${r | 0},${g | 0},${b | 0})`;
}
const store = {
  get(k: string): string | null { try { return localStorage.getItem(k); } catch { return null; } },
  set(k: string, v: string): void { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
};
const session = {
  take(k: string): string | null { try { const v = sessionStorage.getItem(k); sessionStorage.removeItem(k); return v; } catch { return null; } },
  set(k: string, v: string): void { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
};
const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
const status = (e: unknown) => (e instanceof ApiError ? e.status : 0);
// "Top mass: 123" -> Top mass: <b>123</b>, built from text nodes
function statLine(line: string): HTMLElement {
  const div = document.createElement('div'), i = line.indexOf(': ');
  if (i < 0) { div.textContent = line; return div; }
  const b = document.createElement('b');
  b.textContent = line.slice(i + 2);
  // appendChild, not append: the Workers types shadow DOM Element.append with HTMLRewriter's
  div.appendChild(document.createTextNode(line.slice(0, i + 2)));
  div.appendChild(b);
  return div;
}
function need<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} is missing from index.html`);
  return el as T;
}
function shake(el: HTMLElement): void {
  el.classList.remove('shake');
  void el.offsetWidth; // restart the animation
  el.classList.add('shake');
}

// ========== UI ==========
type Patch = { name?: string; skin?: SkinId };

export class UI {
  profile: Profile | null = null;
  mode: PlayMode = 'solo';
  difficulty: DifficultyId = 'normal';
  view: ViewId = '3d';
  room: string | null = null; // private room (invite link or created here)
  challenge: Challenge | null = null; // landed through a friend's "Beat my score" link
  readonly lang: Lang;
  private readonly t: Strings;
  private lastScore: number | null = null; // the last life's top mass, for "Beat my score"

  private selSkin: SkinId = 'blue';
  private loaded = false; // a profile has arrived from the server (not just the local mirror)
  private playing: PlayMode | null = null; // mode of the game in progress, for "Play again"
  private pending: Patch = {}; // name/skin changes not yet sent
  private inflight: Patch | null = null;
  private saving: Promise<void> | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | undefined;
  private deathTimer: ReturnType<typeof setTimeout> | undefined;
  private refreshTimer: ReturnType<typeof setTimeout> | undefined;
  private refreshing: Promise<Profile | null> | null = null;
  private roomReq: Promise<string | null> | null = null;
  private deathPts: number | null = null;
  private deathTotal = true; // the profile's total already includes deathPts (else only "+N" is shown)
  private deathSeq = 0;
  private buying = false;
  private starting = false;
  private readonly el = {
    lobby: need('lobby'), name: need<HTMLInputElement>('name'), points: need('points'), skins: need('skins'),
    diffRow: need('diffRow'), invite: need('invite'), inviteLink: need<HTMLInputElement>('inviteLink'),
    copy: need<HTMLButtonElement>('copyInvite'), play: need<HTMLButtonElement>('play'), paywall: need('paywall'),
    buy: need<HTMLButtonElement>('buy'), code: need<HTMLInputElement>('code'), redeem: need<HTMLButtonElement>('redeem'),
    payMsg: need('payMsg'), msg: need('lobbyMsg'), sound: need<HTMLButtonElement>('sound'),
    death: need('death'), deathTitle: need('deathTitle'), stats: need('stats'), deathPoints: need('deathPoints'),
    again: need<HTMLButtonElement>('again'), toLobby: need<HTMLButtonElement>('toLobby'),
    connecting: need('connecting'), connectingText: need('connectingText'), cancelJoin: need<HTMLButtonElement>('cancelJoin'),
    payModal: need('payModal'), payMount: need('payMount'), payClose: need<HTMLButtonElement>('payClose'),
    codeBox: need('codeBox'), myCode: need<HTMLInputElement>('myCode'), copyCode: need<HTMLButtonElement>('copyCode'),
    codeHint: need('codeHint'),
    challenge: need('challenge'), beatMe: need<HTMLButtonElement>('beatMe'), copyRoom: need<HTMLButtonElement>('copyRoom'),
    shareMsg: need('shareMsg'),
  };
  private embedded: EmbeddedCheckout | null = null;

  constructor(private readonly handlers: UIHandlers, private readonly sound: SoundLike | null = null) {
    const e = this.el;
    // the page's language and, on /play-with-friends, its mode (<main id="lobby" data-lang data-mode>)
    const lang = e.lobby.getAttribute('data-lang');
    this.lang = isLang(lang) ? lang : 'en';
    this.t = STRINGS[this.lang];
    const pageMode = e.lobby.getAttribute('data-mode');
    // instant paint from last visit; the server's answer replaces it in init()
    e.name.value = sanitizeName(store.get('cg.name') || '');
    const d = store.get('cg.diff');
    if (d === 'easy' || d === 'normal' || d === 'hard') this.difficulty = d;
    if (store.get('cg.view') === '2d') this.view = '2d';
    const m = store.get('cg.mode');
    if (m === 'solo' || m === 'online') this.mode = m;
    if (pageMode === 'friends') this.mode = 'friends'; // one click: the room is made on load, Play joins it
    const s = store.get('cg.skin');
    if (isSkinId(s)) this.selSkin = s;
    let cached: Profile | null = null;
    try { cached = toProfile(JSON.parse(store.get('cg.profile') || 'null')); } catch { cached = null; }
    if (cached) this.applyProfile(cached, false);

    e.name.addEventListener('input', () => {
      const name = sanitizeName(e.name.value);
      store.set('cg.name', name);
      if (name) this.queueSave({ name });
    });
    e.name.addEventListener('keydown', ev => { if (ev.key === 'Enter') { ev.preventDefault(); this.click(); void this.play(); } });
    for (const [id, mode] of MODES) need(id).addEventListener('click', () => { this.click(); this.selectMode(mode); });
    for (const [id, diff] of DIFFS) need(id).addEventListener('click', () => { this.click(); this.selectDifficulty(diff); });
    for (const v of ['3d', '2d'] as const) need(`view${v}`).addEventListener('click', () => { this.click(); this.selectView(v); });
    e.copy.addEventListener('click', () => void this.copyInvite());
    e.play.addEventListener('click', () => { this.click(); void this.play(); });
    e.buy.addEventListener('click', () => { this.click(); void this.checkout(); });
    e.payClose.addEventListener('click', () => { this.click(); this.closePayment(); });
    e.payModal.addEventListener('click', ev => { if (ev.target === e.payModal) this.closePayment(); });
    window.addEventListener('keydown', ev => { if (ev.key === 'Escape' && this.embedded) { ev.stopPropagation(); this.closePayment(); } }, true);
    e.redeem.addEventListener('click', () => { this.click(); void this.redeem(); });
    e.copyCode.addEventListener('click', () => { this.click(); void this.copyMyCode(); });
    e.code.addEventListener('keydown', ev => { if (ev.key === 'Enter') { ev.preventDefault(); void this.redeem(); } });
    e.sound.addEventListener('click', () => this.toggleSound());
    e.beatMe.addEventListener('click', () => { this.click(); void this.shareScore(); });
    e.copyRoom.addEventListener('click', () => { this.click(); void this.shareRoom(); });
    e.again.addEventListener('click', () => {
      this.click();
      if (this.handlers.onAgain) this.handlers.onAgain();
      else void this.play(this.playing ?? this.mode);
    });
    e.toLobby.addEventListener('click', () => {
      this.click();
      if (this.handlers.onLobby) this.handlers.onLobby(); else this.showLobby();
    });
    e.cancelJoin.addEventListener('click', () => {
      this.click();
      if (this.handlers.onCancel) this.handlers.onCancel(); else this.showLobby();
    });
    // back from Stripe via the browser's back button: the page comes out of the bfcache with Buy disabled
    window.addEventListener('pageshow', ev => { if (ev.persisted) { e.buy.disabled = false; this.payMsg(''); } });

    this.selectMode(this.mode);
    this.selectDifficulty(this.difficulty);
    this.selectView(this.view, false);
    this.renderSound();
    this.renderSkins();
    this.refreshPaywall();
  }

  // Loads the profile (a first visit gets the session cookie; the row comes with the first change), then handles
  // ?room= invite links and the ?checkout= return from Stripe.
  async init(): Promise<void> {
    const { room, checkout, play } = readLaunchParams(location.search);
    if (checkout) this.replaceQuery(q => q.delete('checkout')); // a reload must not re-run the return flow
    if (play) { this.replaceQuery(q => q.delete('play')); this.selectMode(play); } // app-icon shortcut
    const challenge = readChallenge(location.search);
    if (location.search.includes('score=') || location.search.includes('ref=')) this.replaceQuery(q => { q.delete('score'); q.delete('ref'); });
    if (challenge) this.showChallenge(challenge);
    const room0 = room ?? (checkout ? session.take('cg.room') : null); // Stripe's return URL drops ?room=
    if (room0 && ROOM_CODE_RE.test(room0)) { this.setRoom(room0); this.selectMode('friends'); }
    let p: Profile | null = null;
    for (let i = 0; i < 3 && !p; i++) {
      p = await this.refresh();
      if (!p && i < 2) { this.msg('Can\'t reach the server. Retrying…'); await sleep(1500 * (i + 1)); }
    }
    if (!p) { this.msg('Can\'t reach the server. Check your connection and reload.'); return; }
    this.msg('');
    if (this.mode === 'friends' && !this.room) void this.makeRoom();
    if (checkout) await this.verifyCheckout(checkout);
    if (play && this.mayPlay(play) && !installCardOpen()) void this.play(play); // shortcut: straight into a game
  }

  // Re-reads /api/me (points awarded server-side after online games, purchases, etc.)
  refresh(): Promise<Profile | null> {
    if (!this.refreshing) {
      this.refreshing = api.me()
        .then(p => { this.loaded = true; this.setProfile(p); return p; }, () => null)
        .finally(() => { this.refreshing = null; });
    }
    return this.refreshing;
  }

  setProfile(p: Profile): void { this.applyProfile(p, true); }

  get name(): string { return sanitizeName(this.el.name.value) || this.profile?.name || 'Player'; }
  get skin(): SkinId { return this.selSkin; }

  showLobby(): void {
    clearTimeout(this.deathTimer);
    this.playing = null;
    this.el.death.classList.add('hidden');
    this.el.lobby.classList.remove('hidden');
    this.setConnecting(false);
    void this.refresh();
  }
  hideLobby(): void {
    clearTimeout(this.deathTimer);
    this.el.lobby.classList.add('hidden');
    this.el.death.classList.add('hidden');
  }

  showDeath(d: DeathInfo): void {
    const e = this.el, seq = ++this.deathSeq;
    e.deathTitle.textContent = d.title;
    const stats = [...d.stats];
    if (this.challenge && d.score !== undefined) {
      const left = this.challenge.score - Math.floor(d.score);
      stats.push(`${this.t.challengeLabel}: ${left < 0 ? this.t.beaten : fill(this.t.toGo, { n: Math.max(1, left) })}`);
    }
    e.stats.replaceChildren(...stats.map(statLine));
    this.lastScore = d.score !== undefined && d.score >= 1 ? Math.floor(d.score) : null;
    e.beatMe.classList.toggle('hidden', this.lastScore === null);
    e.copyRoom.classList.toggle('hidden', !(this.playing === 'friends' && this.room));
    this.shareMsg('');
    this.deathPts = d.points ?? null;
    // online, the death event carries the Arena's award, which reaches D1 in the background: "+N" now,
    // the total once the server's profile includes it (never the old total next to the new points)
    this.deathTotal = d.points === undefined;
    this.renderDeathPoints();
    clearTimeout(this.deathTimer);
    e.death.classList.add('hidden');
    this.deathTimer = setTimeout(() => e.death.classList.remove('hidden'), d.delayMs ?? DEATH_DELAY_MS);
    clearTimeout(this.refreshTimer);
    if (d.points === undefined) {
      // solo: the result post answers with the new total (setProfile + setDeathPoints); this is the fallback
      this.refreshTimer = setTimeout(() => void this.refresh(), POINTS_REFRESH_MS);
      return;
    }
    const want = (this.profile?.points ?? 0) + d.points;
    const settle = (p: Profile | null) => {
      if (!p || seq !== this.deathSeq) return;
      this.deathTotal = true;
      this.renderDeathPoints();
    };
    void this.refresh().then(p => {
      if (seq !== this.deathSeq) return;
      if (p && p.points >= want) { settle(p); return; }
      // the award hadn't landed yet: one more look, then show whatever total the server has
      this.refreshTimer = setTimeout(() => void this.refresh().then(settle), POINTS_REFRESH_MS);
    });
  }

  // the life's points once the server has answered (solo posts its result after the death screen is up)
  setDeathPoints(points: number | null): void {
    this.deathPts = points;
    this.deathTotal = true; // the caller has just set the profile that includes them
    this.renderDeathPoints();
  }

  // a line under the lobby buttons (connection lost, room full, ...)
  notify(text: string, ok = false): void { this.msg(text, ok); }

  setConnecting(on: boolean, text = 'Connecting…'): void {
    this.el.connecting.classList.toggle('hidden', !on);
    if (on) this.el.connectingText.textContent = text;
  }

  // sends any pending name/skin change now (online play reads the name from the server)
  async flushSave(): Promise<void> {
    clearTimeout(this.saveTimer);
    while (this.saving) await this.saving;
    const patch = this.pending;
    if (patch.name === undefined && patch.skin === undefined) return;
    this.pending = {};
    this.inflight = patch;
    const run = api.updateMe(patch).then(
      p => { this.inflight = null; this.setProfile(p); },
      (err: unknown) => {
        this.inflight = null;
        const st = status(err);
        // offline / server hiccup / busy (429): keep the change for the next save (newer edits win)
        if (st === 0 || st === 429 || st >= 500) { this.pending = { ...patch, ...this.pending }; return; }
        if (patch.name !== undefined) this.msg('That name isn\'t allowed. Try another.');
        if (patch.skin !== undefined && this.profile && this.pending.skin === undefined) {
          this.selSkin = this.profile.skin; // the server kept the old skin
          this.renderSkins();
        }
      },
    );
    this.saving = run;
    try { await run; } finally { if (this.saving === run) this.saving = null; }
  }

  // ========== lobby ==========
  selectMode(mode: PlayMode): void {
    this.mode = mode;
    if (mode !== 'friends') store.set('cg.mode', mode);
    for (const [id, m] of MODES) {
      const b = need(id);
      b.classList.toggle('sel', m === mode);
      b.setAttribute('aria-pressed', String(m === mode));
    }
    this.el.diffRow.classList.toggle('hidden', mode !== 'solo');
    this.refreshPaywall(); // the Play label, and whether a locked profile can start this mode
    this.renderInvite();
    // a private room is made as soon as you pick the mode, so the link can be shared before you start
    if (mode === 'friends' && !this.room && this.loaded) void this.makeRoom();
  }

  selectView(v: ViewId, notify = true): void {
    this.view = v;
    store.set('cg.view', v);
    for (const x of ['3d', '2d'] as const) {
      const b = need(`view${x}`);
      b.classList.toggle('sel', x === v);
      b.setAttribute('aria-pressed', String(x === v));
    }
    if (notify) this.handlers.onView?.(v);
  }
  // no 3D on this device: the choice is 2D, and 3D can't be picked
  no3d(): void {
    const b = need<HTMLButtonElement>('view3d');
    b.disabled = true; b.title = '3D is not available on this device';
    if (this.view === '3d') { this.view = '2d'; for (const x of ['3d', '2d'] as const) need(`view${x}`).setAttribute('aria-pressed', String(x === '2d')); }
  }

  private selectDifficulty(d: DifficultyId): void {
    this.difficulty = d;
    store.set('cg.diff', d);
    for (const [id, x] of DIFFS) {
      const b = need(id);
      b.classList.toggle('sel', x === d);
      b.setAttribute('aria-pressed', String(x === d));
    }
  }

  private async play(mode: PlayMode = this.mode): Promise<void> {
    if (this.starting) return;
    // Solo vs bots is always free; with FREE_PLAY every mode is (with ads), else online and friends need the unlock
    if (!this.mayPlay(mode)) {
      this.refreshPaywall();
      if (this.profile) shake(this.el.buy);
      return;
    }
    this.starting = true;
    try {
      // online play uses the name saved on the server; solo can save in the background
      if (mode === 'solo') void this.flushSave(); else await this.flushSave();
      const opts: PlayOptions = { difficulty: this.difficulty };
      if (mode === 'friends') {
        const room = await this.makeRoom();
        if (!room) return;
        opts.room = room;
      }
      this.playing = mode;
      this.msg('');
      this.handlers.onPlay(mode, opts);
    } finally {
      this.starting = false;
    }
  }

  private queueSave(patch: Patch): void {
    Object.assign(this.pending, patch);
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => void this.flushSave(), SAVE_DEBOUNCE_MS);
  }

  private mayPlay(mode: PlayMode): boolean {
    const p = this.profile;
    return !!p && (p.unlocked || !!p.free || mode === 'solo');
  }

  private applyProfile(p: Profile, fromServer: boolean): void {
    // entitlements come only with /api/me and unlock replies: every other reply keeps the last ones seen
    if (p.free === undefined && this.profile?.free !== undefined) p = { ...p, free: this.profile.free, ads: this.profile.ads, code: this.profile.code };
    if (p.unlocked) p = { ...p, ads: null };
    this.profile = p;
    if (fromServer) store.set('cg.profile', JSON.stringify(p));
    const nameEl = this.el.name;
    if (!nameEl.value && p.name && document.activeElement !== nameEl) nameEl.value = p.name;
    // the server's skin wins unless a change of mine hasn't been saved yet
    if (fromServer && this.pending.skin === undefined && this.inflight?.skin === undefined) this.selSkin = p.skin;
    if (!this.owns(this.selSkin)) this.selSkin = p.skin;
    this.renderSkins();
    this.refreshPaywall();
    this.renderDeathPoints();
    this.handlers.onProfile?.(p);
  }

  private owns(id: SkinId): boolean {
    const s = SKIN_BY_ID[id];
    return !!s && !s.hidden && (!s.price || !!this.profile?.owned.includes(id));
  }

  // ========== skin shop ==========
  private renderSkins(): void {
    this.el.points.textContent = String(this.profile?.points ?? 0);
    const items: HTMLElement[] = [];
    for (const s of SKINS) {
      const has = this.owns(s.id), sel = s.id === this.selSkin;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'skin' + (sel ? ' sel' : '') + (has ? '' : ' locked');
      b.style.background = s.color; // CSSOM, allowed by the CSP (only style="" attributes are blocked)
      b.style.borderColor = sel ? '#f8fafc' : darken(s.color, 0.78);
      if (s.emoji) b.appendChild(document.createTextNode(s.emoji));
      if (!has) {
        const price = document.createElement('span');
        price.className = 'price';
        price.textContent = String(s.price);
        b.appendChild(price);
      }
      b.title = has ? s.id : `${s.id} - ${s.price} points`;
      b.setAttribute('aria-label', has ? `${s.id} skin` : `${s.id} skin, ${s.price} points`);
      b.setAttribute('aria-pressed', String(sel));
      b.addEventListener('click', () => void this.pickSkin(s.id, b));
      items.push(b);
    }
    this.el.skins.replaceChildren(...items);
  }

  private async pickSkin(id: SkinId, el: HTMLElement): Promise<void> {
    this.click();
    const s = SKIN_BY_ID[id], p = this.profile;
    if (!s || s.hidden) return;
    if (!this.owns(id)) {
      if (!p || p.points < s.price || this.buying) { shake(el); return; }
      this.buying = true;
      el.classList.add('busy');
      try {
        this.setProfile(await api.buySkin(id)); // the server deducts the points
      } catch (err) {
        shake(el);
        const st = status(err);
        this.msg(st >= 400 && st < 500 ? 'Not enough points for that skin.' : 'Couldn\'t reach the shop. Try again.');
        return;
      } finally {
        this.buying = false;
        el.classList.remove('busy');
      }
      if (!this.owns(id)) return;
      this.handlers.onPurchase?.(id);
    }
    this.selSkin = id;
    store.set('cg.skin', id);
    this.queueSave({ skin: id });
    this.renderSkins();
  }

  // ========== private rooms ==========
  private makeRoom(): Promise<string | null> {
    if (this.room) return Promise.resolve(this.room);
    if (!this.roomReq) {
      this.msg('Creating a private room…', true);
      this.roomReq = api.createRoom()
        .then(r => { this.setRoom(r.room); this.msg(''); return r.room; }, err => {
          const st = status(err);
          this.msg(st === 401 || st === 403 ? 'Unlock the game to play with friends.' : 'Couldn\'t create a room. Try again.');
          return null;
        })
        .finally(() => { this.roomReq = null; });
    }
    return this.roomReq;
  }

  private setRoom(room: string): void {
    this.room = room;
    this.renderInvite();
    this.replaceQuery(q => q.set('room', room)); // a reload rejoins the same room
  }

  private renderInvite(): void {
    const show = this.mode === 'friends' && !!this.room;
    this.el.invite.classList.toggle('hidden', !show);
    if (this.room) this.el.inviteLink.value = inviteLink(this.room, location.origin);
  }

  private async copyInvite(): Promise<void> {
    this.click();
    const input = this.el.inviteLink, link = input.value;
    if (!link) return;
    let ok = false;
    try { await navigator.clipboard.writeText(link); ok = true; } catch { ok = false; }
    if (!ok) {
      input.select();
      input.setSelectionRange?.(0, link.length); // iOS ignores select()
      try { ok = document.execCommand('copy'); } catch { ok = false; }
    }
    this.el.copy.textContent = ok ? 'Copied!' : copyKeyHint(typeof navigator !== 'undefined' ? navigator as NavLike : undefined);
    setTimeout(() => { this.el.copy.textContent = 'Copy'; }, COPIED_MS);
  }

  // ========== invite loop: challenge banner, "Beat my score", room link ==========
  private showChallenge(c: Challenge): void {
    this.challenge = c;
    this.el.challenge.textContent = fill(this.t.challenge, { name: c.by || '?', score: c.score });
    this.el.challenge.classList.remove('hidden');
  }

  private async shareScore(): Promise<void> {
    if (this.lastScore === null) return;
    const room = this.playing === 'friends' ? this.room : null;
    const link = challengeLink(location.origin, { by: this.name, score: this.lastScore, room });
    await this.share(link, fill(this.t.share, { score: this.lastScore }), this.t.copied);
  }
  private async shareRoom(): Promise<void> {
    if (!this.room) return;
    await this.share(inviteLink(this.room, location.origin), '', this.t.roomCopied);
  }
  // phones: the share sheet (cancelling it is fine); elsewhere, or if sharing fails: the clipboard;
  // if that is blocked too, the link is shown to copy by hand
  private async share(url: string, text: string, done: string): Promise<void> {
    const nav = (typeof navigator !== 'undefined' ? navigator : undefined) as (Navigator & { share?: (d: ShareData) => Promise<void> }) | undefined;
    const coarse = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
    if (coarse && typeof nav?.share === 'function') {
      try { await nav.share(text ? { title: 'CellGame.io', text, url } : { title: 'CellGame.io', url }); return; } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
      }
    }
    let ok = false;
    try { await nav?.clipboard.writeText(url); ok = !!nav?.clipboard; } catch { ok = false; }
    this.shareMsg(ok ? done : `${this.t.copyFailed} ${url}`);
  }
  private shareMsg(text: string): void {
    this.el.shareMsg.textContent = text;
  }

  // ========== paywall ==========
  // FREE_PLAY: Play is always there and the $5 removes ads. Without it: solo is free, the rest needs the unlock.
  private refreshPaywall(): void {
    const p = this.profile, unlocked = !!p?.unlocked, free = !!p?.free;
    this.el.play.classList.toggle('hidden', !!p && !this.mayPlay(this.mode));
    this.el.play.disabled = !p; // still loading
    this.el.play.textContent = p && !unlocked && !free && this.mode === 'solo' ? this.t.playFree : this.t[PLAY_KEY[this.mode]];
    this.el.paywall.classList.toggle('hidden', !p || unlocked);
    this.el.buy.textContent = free ? this.t.removeAds : this.t.unlock;
    this.renderMyCode();
  }

  // The buyer's one-time code: removes ads on one other browser or device (or this one after clearing cookies).
  private renderMyCode(): void {
    const c = this.profile?.unlocked ? this.profile.code : null;
    this.el.codeBox.classList.toggle('hidden', !c);
    if (!c) return;
    this.el.myCode.value = c.code;
    this.el.copyCode.disabled = c.used;
    this.el.codeHint.textContent = c.used
      ? 'Your one-time code has been used on another device.'
      : 'Your one-time code: use it once on another device or browser, or here if you clear your cookies. Keep it safe.';
  }
  private async copyMyCode(): Promise<void> {
    const code = this.el.myCode.value;
    if (!code) return;
    let ok = false;
    try { await navigator.clipboard.writeText(code); ok = true; } catch {
      this.el.myCode.select();
      try { ok = document.execCommand('copy'); } catch { ok = false; }
    }
    this.el.copyCode.textContent = ok ? 'Copied!' : 'Copy';
    setTimeout(() => { this.el.copyCode.textContent = 'Copy'; }, COPIED_MS);
  }

  // Pay inside the game: Stripe's Embedded Checkout in a window over the lobby. Cards finish in place and
  // onComplete verifies with the Worker; if Stripe.js can't load, fall back to the hosted checkout page.
  private async checkout(): Promise<void> {
    const b = this.el.buy;
    if (b.disabled) return;
    b.disabled = true;
    this.payMsg('Opening secure checkout…', true);
    try {
      const [{ clientSecret, sessionId, publishableKey }, Stripe] = await Promise.all([api.checkoutEmbedded(), loadStripe()]);
      if (this.room) session.set('cg.room', this.room); // redirect-based methods come back through ?checkout=
      const stripe = Stripe(publishableKey), opts: EmbeddedOpts = {
        fetchClientSecret: () => Promise.resolve(clientSecret),
        onComplete: () => { this.closePayment(); void this.verifyCheckout(sessionId); },
      };
      const create = stripe.createEmbeddedCheckoutPage ?? stripe.initEmbeddedCheckout;
      if (!create) throw new Error('Stripe.js has no embedded checkout');
      const ec = await create.call(stripe, opts);
      this.embedded = ec;
      this.el.payModal.classList.remove('hidden');
      ec.mount(this.el.payMount);
      this.payMsg('');
      b.disabled = false;
      return;
    } catch (err) {
      const st = status(err);
      if (st >= 400 && st < 500) { b.disabled = false; this.payMsg(st === 429 ? 'Too many tries. Please wait a minute.' : 'Couldn\'t open checkout. Please try again.'); return; }
      this.closePayment(); // Stripe.js blocked or the embedded session failed: use the hosted page instead
    }
    try {
      const { url } = await api.checkout();
      if (!isStripeCheckoutUrl(url)) throw new Error('unexpected checkout URL');
      if (this.room) session.set('cg.room', this.room);
      location.href = url; // Buy stays disabled while the page unloads
    } catch {
      b.disabled = false;
      this.payMsg('Couldn\'t open checkout. Please try again.');
    }
  }

  private closePayment(): void {
    try { this.embedded?.destroy(); } catch { /* already gone */ }
    this.embedded = null;
    this.el.payModal.classList.add('hidden');
    this.el.payMount.replaceChildren();
  }

  // Back from Stripe with ?checkout=cs_...: never trust the redirect, ask the Worker to verify the session.
  private async verifyCheckout(sessionId: string): Promise<void> {
    if (this.profile?.unlocked) { this.unlockedNow('Payment received. Unlocked. Have fun!'); return; }
    this.payMsg('Confirming your payment…', true);
    for (let i = 0; i < VERIFY_TRIES; i++) {
      try {
        const r = await api.verifyCheckout(sessionId);
        if (r.profile) this.setProfile(r.profile);
        if (r.unlocked || this.profile?.unlocked) { this.unlockedNow('Payment received. Unlocked. Have fun!'); return; }
      } catch (err) {
        const st = status(err);
        if (st >= 400 && st < 500 && st !== 429) break; // not this player's session, or unknown: retrying won't help
      }
      if (i < VERIFY_TRIES - 1) await sleep(VERIFY_RETRY_MS); // the webhook may land a moment later
    }
    const p = await this.refresh();
    if (p?.unlocked) this.unlockedNow('Payment received. Unlocked. Have fun!');
    else this.payMsg('We couldn\'t confirm the payment yet. If you were charged, reload in a minute.');
  }

  private async redeem(): Promise<void> {
    const input = this.el.code, code = input.value.trim().toUpperCase();
    if (!code || code.length > 64) { this.payMsg('That code is not valid.'); shake(input); return; }
    if (this.el.redeem.disabled) return;
    this.el.redeem.disabled = true;
    try {
      const r = await api.redeem(code);
      if (r.profile) this.setProfile(r.profile); else await this.refresh();
      if (r.unlocked || this.profile?.unlocked) { input.value = ''; this.unlockedNow('Unlocked. Have fun!'); }
      else { this.payMsg('That code is not valid.'); shake(input); }
    } catch (err) {
      const st = status(err);
      this.payMsg(st === 409 ? 'That code has already been used.' : st === 429 ? 'Too many tries. Please wait a while and try again.'
        : st === 0 || st >= 500 ? 'Couldn\'t reach the server. Try again.' : 'That code is not valid.');
      shake(input);
    } finally {
      this.el.redeem.disabled = false;
    }
  }

  private unlockedNow(text: string): void {
    this.refreshPaywall();
    this.payMsg('');
    if (this.profile?.free) text = text.replace('Unlocked.', this.profile.code ? 'Ads removed. Your one-time code is below.' : 'Ads removed.');
    this.msg(text, true); // the paywall (and its message line) is hidden now
    this.handlers.onUnlock?.();
  }

  // ========== death screen, sound, messages ==========
  private renderDeathPoints(): void {
    const el = this.el.deathPoints, pts = this.deathPts;
    if (pts === null) { el.replaceChildren(); return; }
    const b = document.createElement('b');
    b.textContent = `+${pts}`;
    el.replaceChildren('Points earned: ', b, this.profile && this.deathTotal ? ` (total ${this.profile.points})` : '');
  }

  private toggleSound(): void {
    const s = this.sound;
    if (!s) return;
    s.setMuted(!s.muted);
    this.renderSound();
    s.click(); // silent when just muted
  }
  private renderSound(): void {
    const b = this.el.sound, s = this.sound;
    b.classList.toggle('hidden', !s);
    if (!s) return;
    b.textContent = s.muted ? '🔇' : '🔊';
    b.title = s.muted ? 'Sound off' : 'Sound on';
    b.setAttribute('aria-label', s.muted ? 'Sound off, click to turn on' : 'Sound on, click to mute');
    b.setAttribute('aria-pressed', String(!s.muted));
  }
  private click(): void { this.sound?.click(); }

  private msg(text: string, ok = false): void {
    this.el.msg.textContent = text;
    this.el.msg.className = 'payMsg' + (ok ? ' ok' : '');
  }
  private payMsg(text: string, ok = false): void {
    this.el.payMsg.textContent = text;
    this.el.payMsg.className = 'payMsg' + (ok ? ' ok' : '');
  }
  private replaceQuery(edit: (q: URLSearchParams) => void): void {
    const q = new URLSearchParams(location.search);
    edit(q);
    const s = q.toString();
    try { history.replaceState(null, '', location.pathname + (s ? '?' + s : '') + location.hash); } catch { /* sandboxed */ }
  }
}
